import { SCALE, type LmsrQuote, type LmsrSide } from "./lmsrMath";

const BPS_DENOMINATOR = 10_000;
const AFFORDABILITY_EPSILON = 0.000001;

const toTokenAmount = (microAmount: number): number => microAmount / SCALE;

export interface MultiOutcomeMarketSellEstimate {
  gross_refund: number;
  fee: number;
  payout: number;
  new_odds_after_sale: {
    yes: number;
    no: number;
  };
}

const categoricalCost = (q: number[], b: number): number => {
  if (b <= 0 || q.length === 0) {
    return 0;
  }

  const bFloat = b;
  let maxRatio = q[0] / bFloat;
  for (let i = 1; i < q.length; i += 1) {
    const ratio = q[i] / bFloat;
    if (ratio > maxRatio) {
      maxRatio = ratio;
    }
  }

  let sum = 0;
  for (let i = 0; i < q.length; i += 1) {
    sum += Math.exp(q[i] / bFloat - maxRatio);
  }

  return bFloat * (maxRatio + Math.log(sum));
};

export const buildMoynDeltas = (
  outcomeCount: number,
  outcomeId: number,
  sideIsYes: boolean,
  deltaQ: number,
): number[] => {
  const deltas = new Array(outcomeCount).fill(0);

  if (sideIsYes) {
    deltas[outcomeId] = deltaQ;
    return deltas;
  }

  for (let i = 0; i < outcomeCount; i += 1) {
    if (i !== outcomeId) {
      deltas[i] = deltaQ;
    }
  }

  return deltas;
};

export const computeCategoricalBuyCost = (
  q: number[],
  b: number,
  deltas: number[],
): number => {
  if (b <= 0 || q.length === 0 || q.length !== deltas.length) {
    return 0;
  }

  const after = new Array(q.length);
  for (let i = 0; i < q.length; i += 1) {
    if (deltas[i] < 0) {
      return 0;
    }
    after[i] = q[i] + deltas[i];
  }

  const cost = categoricalCost(after, b) - categoricalCost(q, b);
  if (cost <= 0) {
    return 0;
  }

  return Math.floor(cost);
};

const computeCategoricalRefund = (
  q: number[],
  b: number,
  deltas: number[],
): number => {
  if (b <= 0 || q.length === 0 || q.length !== deltas.length) {
    return 0;
  }

  const after = new Array(q.length);
  for (let i = 0; i < q.length; i += 1) {
    if (deltas[i] < 0 || q[i] < deltas[i]) {
      return 0;
    }
    after[i] = q[i] - deltas[i];
  }

  const refund = categoricalCost(q, b) - categoricalCost(after, b);
  if (refund <= 0) {
    return 0;
  }

  return Math.floor(refund);
};

const categoricalTotalCharge = (
  q: number[],
  b: number,
  deltasTemplate: number[],
  deltaQ: number,
  feeBps: number,
): number => {
  const deltas = deltasTemplate.map((value) => (value === 0 ? 0 : deltaQ));
  const rawCost = computeCategoricalBuyCost(q, b, deltas);
  return rawCost * (1 + feeBps / BPS_DENOMINATOR);
};

export const computeCategoricalSharesFromBudget = (
  q: number[],
  b: number,
  deltasTemplate: number[],
  budget: number,
  feeBps: number,
): number => {
  if (
    budget <= 0 ||
    b <= 0 ||
    q.length === 0 ||
    q.length !== deltasTemplate.length
  ) {
    return 0;
  }

  const budgetFloat = budget;
  let low = 0;
  let high = 1;

  while (true) {
    if (
      categoricalTotalCharge(q, b, deltasTemplate, high, feeBps) >
      budgetFloat + AFFORDABILITY_EPSILON
    ) {
      break;
    }

    low = high;
    const nextHigh = high * 2;
    if (nextHigh <= high) {
      return high;
    }
    high = nextHigh;
  }

  while (low + 1 < high) {
    const mid = low + Math.trunc((high - low) / 2);
    if (
      categoricalTotalCharge(q, b, deltasTemplate, mid, feeBps) <=
      budgetFloat + AFFORDABILITY_EPSILON
    ) {
      low = mid;
    } else {
      high = mid;
    }
  }

  return low;
};

export const computeCategoricalPrice = (
  q: number[],
  b: number,
  outcomeId: number,
): number => {
  if (b <= 0 || q.length === 0 || outcomeId < 0 || outcomeId >= q.length) {
    return 0;
  }

  const bFloat = b;
  let maxRatio = q[0] / bFloat;
  for (let i = 1; i < q.length; i += 1) {
    const ratio = q[i] / bFloat;
    if (ratio > maxRatio) {
      maxRatio = ratio;
    }
  }

  let sum = 0;
  for (let i = 0; i < q.length; i += 1) {
    sum += Math.exp(q[i] / bFloat - maxRatio);
  }

  const selected = Math.exp(q[outcomeId] / bFloat - maxRatio);
  return Math.floor((selected / sum) * SCALE);
};

export const estimateMultiOutcomeBuyQuote = (
  q: number[],
  b: number,
  outcomeId: number,
  side: LmsrSide,
  spendAmount: number,
  feeBps: number,
): LmsrQuote | null => {
  if (
    spendAmount <= 0 ||
    b <= 0 ||
    q.length === 0 ||
    outcomeId < 0 ||
    outcomeId >= q.length
  ) {
    return null;
  }

  const sideIsYes = side === "yes";
  const grossSpendMicro = Math.trunc(spendAmount * SCALE);
  const feeAmountMicro = Math.trunc(
    (grossSpendMicro * feeBps) / BPS_DENOMINATOR,
  );
  const budgetWithoutFeeMicro = grossSpendMicro - feeAmountMicro;
  const deltasTemplate = buildMoynDeltas(q.length, outcomeId, sideIsYes, 1);
  const deltaQ = computeCategoricalSharesFromBudget(
    q,
    b,
    deltasTemplate,
    budgetWithoutFeeMicro,
    0,
  );
  const deltas = buildMoynDeltas(q.length, outcomeId, sideIsYes, deltaQ);
  const qAfter = q.map((value, index) => value + deltas[index]);
  const currentProbability = computeCategoricalPrice(q, b, outcomeId) / SCALE;
  const nextProbability = computeCategoricalPrice(qAfter, b, outcomeId) / SCALE;

  return {
    estimated_shares: toTokenAmount(deltaQ),
    raw_cost: toTokenAmount(budgetWithoutFeeMicro),
    fee: toTokenAmount(feeAmountMicro),
    total_charge: toTokenAmount(grossSpendMicro),
    refund: 0,
    avg_price_per_share: deltaQ > 0 ? grossSpendMicro / deltaQ : 0,
    min_shares_for_slippage: Math.trunc((deltaQ * 0.98) / SCALE),
    current_odds: {
      yes: currentProbability * 100,
      no: (1 - currentProbability) * 100,
    },
    new_odds_after_purchase: {
      yes: nextProbability * 100,
      no: (1 - nextProbability) * 100,
    },
    exceeds_liquidity_cap: false,
  };
};

export const estimateMultiOutcomeSellQuote = (
  q: number[],
  b: number,
  outcomeId: number,
  side: LmsrSide,
  sharesAmount: number,
  feeBps: number,
): MultiOutcomeMarketSellEstimate | null => {
  if (
    sharesAmount <= 0 ||
    b <= 0 ||
    q.length === 0 ||
    outcomeId < 0 ||
    outcomeId >= q.length
  ) {
    return null;
  }

  const sharesMicro = Math.round(sharesAmount * SCALE);
  if (sharesMicro <= 0) {
    return null;
  }

  const deltas = buildMoynDeltas(
    q.length,
    outcomeId,
    side === "yes",
    sharesMicro,
  );

  for (let i = 0; i < q.length; i += 1) {
    if (q[i] < deltas[i]) {
      return null;
    }
  }

  const grossRefundMicro = computeCategoricalRefund(q, b, deltas);
  const sellFeeMicro = Math.trunc((grossRefundMicro * feeBps) / BPS_DENOMINATOR);
  const netPayoutMicro = grossRefundMicro - sellFeeMicro;
  const qAfter = q.map((value, index) => value - deltas[index]);
  const nextProbability = computeCategoricalPrice(qAfter, b, outcomeId) / SCALE;

  return {
    gross_refund: toTokenAmount(grossRefundMicro),
    fee: toTokenAmount(sellFeeMicro),
    payout: toTokenAmount(netPayoutMicro),
    new_odds_after_sale: {
      yes: nextProbability * 100,
      no: (1 - nextProbability) * 100,
    },
  };
};
